import { AIGeneratorContext } from '../components/AIGeneratorModal/interfaces'
import { AILessonPlanPayload } from '../service/aiLessonPlanService'
import { SelectedProjectItems } from './projectItemsHelpers'

/**
 * Extrai objetivos da string HTML ou array
 */
function extractObjectives(objectives: string | string[] | undefined): string[] {
  if (!objectives) return []

  if (Array.isArray(objectives)) {
    return objectives
  }

  // Se for string HTML, extrair os itens de lista
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = objectives
  const listItems = tempDiv.querySelectorAll('li')

  if (listItems.length > 0) {
    return Array.from(listItems).map(li => li.textContent || '').filter(Boolean)
  }

  // Se não tiver <li>, retornar como array com um item
  const text = tempDiv.textContent || objectives
  return text ? [text] : []
}

/**
 * Extrai etapas da string HTML ou array
 */
function extractStages(stages: string | string[] | any[] | undefined): string[] {
  if (!stages) return []

  if (Array.isArray(stages)) {
    // Se for array de objetos com stage e description
    if (stages.length > 0 && typeof stages[0] === 'object' && 'description' in stages[0]) {
      return stages.map(s => s.description).filter(Boolean)
    }
    // Se for array de strings
    return stages.filter(s => typeof s === 'string')
  }

  // Se for string HTML, extrair os itens de lista
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = stages
  const listItems = tempDiv.querySelectorAll('li')

  if (listItems.length > 0) {
    return Array.from(listItems).map(li => li.textContent || '').filter(Boolean)
  }

  // Se não tiver <li>, retornar como array com um item
  const text = tempDiv.textContent || stages
  return text ? [text] : []
}

/**
 * Remove tags HTML de uma string
 */
function stripHtml(html: string | undefined): string {
  if (!html) return ''
  const tempDiv = document.createElement('div')
  tempDiv.innerHTML = html
  return tempDiv.textContent || tempDiv.innerText || ''
}

/**
 * Transforma o aiContext em payload para a API de IA de lições
 * @param aiContext - Contexto de IA do Step 1
 * @param numberOfMeetings - Número de encontros a serem criados
 * @param startDate - Data de início
 * @param selectedItems - Items curriculares selecionados no Step 3 (OBRIGATÓRIO)
 */
export function transformAIContextToLessonPlanPayload(
  aiContext: AIGeneratorContext,
  numberOfMeetings: number,
  startDate: string,
  selectedItems: SelectedProjectItems
): AILessonPlanPayload {
  const generatedData = aiContext.generatedData
  const fullApiResponse = aiContext.fullApiResponse

  if (!generatedData) {
    throw new Error('Dados gerados pela IA não encontrados no contexto')
  }

  // Extrair objetivos
  const objectives = fullApiResponse?.objectives
    ? extractObjectives(fullApiResponse.objectives)
    : extractObjectives(generatedData.objectives)

  // Extrair etapas
  const stages = fullApiResponse?.stages
    ? extractStages(fullApiResponse.stages)
    : extractStages(generatedData.stages)

  // ✅ USAR ITEMS SELECIONADOS DO STEP 3 (não do fullApiResponse)
  return {
    number_meetings: numberOfMeetings,
    date_start_at: startDate,
    name: stripHtml(generatedData.name),
    final_product_kind: generatedData.finalProductKind || fullApiResponse?.final_product_kind || '',
    purpose: stripHtml(generatedData.purpose),
    objectives,
    final_product_description: stripHtml(
      generatedData.finalProductDescription || fullApiResponse?.final_product_description || ''
    ),
    stages,
    synopsis: stripHtml(generatedData.synopsis),
    project_items: {
      contents: selectedItems.contents,
      specific_skills: selectedItems.specific_skills,
    },
  }
}

/**
 * Valida se o aiContext possui os dados necessários
 */
export function validateAIContextForLessonPlan(aiContext: AIGeneratorContext | null): boolean {
  if (!aiContext) return false
  if (!aiContext.generatedData) return false

  const data = aiContext.generatedData

  // Verifica campos essenciais
  return !!(
    data.name &&
    data.purpose &&
    data.synopsis
  )
}
